import React from 'react';
import Icon from '../shared/Icon';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import { combineUploadOutcomes, notifyRejectedUploads, uploadSingleFile } from '../../../utils/uploadOutcome';
import './DataSourceWizard.css';
import Step2ConnectRawData from './steps/Step2ConnectRawData';
import Step2CreateDataset from './steps/Step2CreateDataset';
import Step3PreviewQA from './steps/Step3PreviewQA';

/**
 * View Component for Data Source Wizard
 */
const DataSourceWizard = ({
  isOpen,
  currentStep,
  totalSteps,
  datasetId,
  datasetIds,
  formData,
  selectedFiles,
  promptValidated,
  validationFeedback,
  refinementNotes,
  onClose,
  onNext,
  onBack,
  onBackdropClick,
  onCreateDataset,
  onUploadFiles,
  onExtractFields,
  onRefinePrompt,
  onSetFormData,
  onSetSelectedFiles,
  onRefinementNotesChange,
  onClearFormData,
  onPublishDataset,
  onSaveAsDraft,
  onShowNotification,
  onSetDatasetIds,
}) => {
  const [step1Completed, setStep1Completed] = React.useState(false);
  const [step2Loading, setStep2Loading] = React.useState(false);
  const [step2Completed, setStep2Completed] = React.useState(false);
  // PRD-979: keep the "Next" label honest during a large upload.
  const [step2ProgressLabel, setStep2ProgressLabel] = React.useState('');
  // PRD-979: gates the fire-and-navigate flow. Flag OFF = pre-PRD-979 behaviour
  // (Step 2 awaits createDataset + uploadFiles; Step 3 awaits extractFields).
  const autoPublishEnabled = useFlag('dataset_auto_publish');

  // Check if Step 1 is completed (connection details filled OR files uploaded)
  React.useEffect(() => {
    const connectionData = formData?.connectionData;
    if (connectionData) {
      if (connectionData.selectedSource === 'connection') {
        // Check if connection details are filled
        const isComplete = connectionData.connectionType === 'azure_blob' &&
          connectionData.storageAccount &&
          connectionData.containerName &&
          connectionData.sasToken;
        setStep1Completed(isComplete);
      } else if (connectionData.selectedSource === 'local') {
        // Check if files are selected
        setStep1Completed(selectedFiles && selectedFiles.length > 0);
      }
    } else {
      setStep1Completed(false);
    }
  }, [formData?.connectionData, selectedFiles]);

  // Reset step 2 states when going back to step 2 or earlier
  React.useEffect(() => {
    if (currentStep <= 2) {
      setStep2Loading(false);
      if (currentStep < 2) {
        setStep2Completed(false);
      }
    }
  }, [currentStep]);

  if (!isOpen) return null;

  const steps = [
    { number: 1, label: 'Connect Data' },
    { number: 2, label: 'Create Dataset' },
    { number: 3, label: 'Review and Publish' },
  ];

  const handleClose = async () => {
    // Save as draft when cancelled — handle both single and multi-dataset creation
    const ids = datasetIds?.length > 0 ? datasetIds : (datasetId ? [datasetId] : []);
    if (ids.length > 0 && onSaveAsDraft) {
      for (const id of ids) {
        try {
          await onSaveAsDraft(id);
        } catch (error) {
          console.error('Error saving as draft:', error);
        }
      }
    }
    // Clear form data when wizard is closed
    onClearFormData();
    onClose();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step2ConnectRawData
            datasetId={datasetId}
            selectedFiles={selectedFiles}
            onUploadFiles={onUploadFiles}
            onSetSelectedFiles={onSetSelectedFiles}
            formData={formData}
            onSetFormData={onSetFormData}
          />
        );
      case 2:
        return (
          <Step2CreateDataset
            selectedFiles={selectedFiles}
            formData={formData}
            onSetFormData={onSetFormData}
          />
        );
      case 3:
        return (
          <Step3PreviewQA
            refinementNotes={refinementNotes}
            onRefinementNotesChange={onRefinementNotesChange}
            connectionData={formData?.connectionData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="wizard-page">
      <div className="wizard-container">
        <div className="wizard-header">
          <div className="wizard-breadcrumb">
            <span onClick={() => onClose()}>Data sources</span>
            <span className="wizard-breadcrumb-separator">/</span>
            <span>New dataset</span>
          </div>
          <div className="wizard-header-content">
            <h2 className="wizard-title">New dataset</h2>
            <button className="wizard-close" onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>

        <div className="wizard-steps">
          {steps.map((step) => {
            const isActive = currentStep === step.number;
            let isCompleted = false;
            let isLoading = false;
            let showContent = step.number;

            if (step.number === 1) {
              // Step 1: Show tick if completed (even if still on step 1)
              isCompleted = step1Completed;
              if (isCompleted) {
                showContent = 'check';
              }
            } else if (step.number === 2) {
              // Step 2: Show loading when processing, tick when completed
              isLoading = step2Loading;
              isCompleted = step2Completed;
              if (isLoading) {
                showContent = 'loading';
              } else if (isCompleted) {
                showContent = 'check';
              }
            } else {
              // Step 3: Completed if we've passed it
              isCompleted = currentStep > step.number;
              if (isCompleted) {
                showContent = 'check';
              }
            }

            return (
              <div
                key={step.number}
                className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLoading ? 'loading' : ''}`}
              >
                <div className="wizard-step-number">
                  {showContent === 'loading' ? (
                    <Icon name="refresh" className="wizard-step-loading" size={18} />
                  ) : showContent === 'check' ? (
                    <Icon name="check" className="wizard-step-check" size={18} />
                  ) : (
                    step.number
                  )}
                </div>
                <div className="wizard-step-label">{step.label}</div>
              </div>
            );
          })}
        </div>

        <div className="wizard-content">{renderStepContent()}</div>

        <div className="wizard-actions">
          {currentStep === 1 ? (
            // Step 1 (Connect data): Cancel on left, Next button on right (disabled until files uploaded or connection details provided)
            <>
              <button className="wizard-button wizard-button-secondary" onClick={handleClose}>
                Cancel
              </button>
              <div className="wizard-actions-right">
                <button
                  className="wizard-button wizard-button-primary"
                  onClick={onNext}
                  disabled={!step1Completed}
                >
                  Next
                </button>
              </div>
            </>
          ) : currentStep === 2 ? (
            // Step 2 (Create Dataset): Cancel on left, Back and Next on right
            <>
              <button className="wizard-button wizard-button-secondary" onClick={handleClose}>
                Cancel
              </button>
              <div className="wizard-actions-right">
                <button className="wizard-button wizard-button-secondary" onClick={onBack}>
                  Back
                </button>
                <button
                  className="wizard-button wizard-button-primary"
                  onClick={async () => {
                    // If datasets were already created (e.g. user went back from step 3),
                    // skip re-creation and just advance
                    if (datasetIds?.length > 0) {
                      setStep2Completed(true);
                      onNext();
                      return;
                    }

                    if (!onCreateDataset) { onNext(); return; }

                    const groupAsOne = formData.groupAsOneDataset;
                    const fileDatasetNames = formData.fileDatasetNames || [];
                    const connectionData = formData.connectionData;
                    const selectedModuleId = formData.selectedModuleId || null;

                    // Module path delegates everything (dataset rows,
                    // naming, ingestion) to the per-file fan-out endpoint.
                    // Skips the name validation — backend derives names from
                    // file names with collision suffixes.
                    if (selectedModuleId) {
                      if (!selectedFiles?.length) {
                        if (onShowNotification) onShowNotification('Please attach at least one file.', 'error');
                        return;
                      }
                      try {
                        setStep2Loading(true);
                        const resp = await apiService.uploadFilesToModule(selectedModuleId, selectedFiles);
                        const results = resp?.results || [];
                        const succeeded = results.filter((r) => !r.error);
                        const failed = results.filter((r) => r.error);
                        if (failed.length && onShowNotification) {
                          onShowNotification(
                            `${succeeded.length}/${results.length} uploaded; ${failed.length} failed: ${failed.map((r) => r.file_name).join(', ')}`,
                            failed.length === results.length ? 'error' : 'alert',
                          );
                        }
                        if (onSetDatasetIds) onSetDatasetIds(succeeded.map((r) => r.dataset_id));
                        setStep2Loading(false);
                        setStep2Completed(true);
                        // Skip Step 3 (review) for module uploads — they're
                        // already processed by the standard pipeline.
                        setTimeout(() => { onNext(); }, 500);
                      } catch (error) {
                        setStep2Loading(false);
                        if (onShowNotification) {
                          onShowNotification(error.message || 'Failed to upload to module.', 'error');
                        }
                      }
                      return;
                    }

                    // Validate: need at least one non-empty name
                    const nameForGrouped = formData.groupedDatasetName?.trim() || fileDatasetNames[0]?.trim() || '';
                    if (groupAsOne && !nameForGrouped) {
                      if (onShowNotification) onShowNotification('Please enter a dataset name.', 'error');
                      return;
                    }
                    if (!groupAsOne && fileDatasetNames.some((n) => !n?.trim())) {
                      if (onShowNotification) onShowNotification('All file dataset names must be filled in.', 'error');
                      return;
                    }

                    // PRD-979: with the auto-publish flow, Step 2 is validation-only
                    // and everything runs from Step 3's Publish (fire-and-navigate).
                    // Flag OFF → original inline behaviour (createDataset + uploadFiles
                    // awaited here, then advance to Step 3).
                    if (autoPublishEnabled) {
                      setStep2Completed(true);
                      onNext();
                      return;
                    }
                    try {
                      setStep2Loading(true);
                      const createdIds = [];
                      if (groupAsOne) {
                        const resp = await onCreateDataset({
                          name: nameForGrouped,
                          description: nameForGrouped,
                          prompt: '',
                        });
                        if (resp?.id) {
                          createdIds.push(resp.id);
                          if (connectionData?.selectedSource === 'connection') {
                            const storageUrl = `https://${connectionData.storageAccount}.blob.core.windows.net/${connectionData.containerName}${connectionData.pathPrefix ? '/' + connectionData.pathPrefix : ''}`;
                            await onUploadFiles(resp.id, [], 'azure', storageUrl, connectionData.sasToken);
                          } else if (selectedFiles?.length > 0) {
                            // PRD-1628: a 207 kept the good files; name the rejected ones.
                            notifyRejectedUploads(await onUploadFiles(resp.id, selectedFiles, 'local', '', ''), onShowNotification);
                          }
                        }
                      } else {
                        // PRD-1628: a rejected file doesn't stop the files after it.
                        const outcomes = [];
                        for (let i = 0; i < selectedFiles.length; i++) {
                          const datasetName = (fileDatasetNames[i] || selectedFiles[i].name).trim();
                          const resp = await onCreateDataset({
                            name: datasetName,
                            description: datasetName,
                            prompt: '',
                          });
                          if (resp?.id) {
                            createdIds.push(resp.id);
                            outcomes.push(await uploadSingleFile(selectedFiles[i], () => onUploadFiles(resp.id, [selectedFiles[i]], 'local', '', '')));
                          }
                        }
                        notifyRejectedUploads(combineUploadOutcomes(outcomes), onShowNotification);
                      }
                      if (onSetDatasetIds) onSetDatasetIds(createdIds);
                      setStep2Loading(false);
                      setStep2Completed(true);
                      setTimeout(() => { onNext(); }, 500);
                    } catch (error) {
                      setStep2Loading(false);
                      if (onShowNotification) {
                        const msg = error?.responseData?.detail || error?.message || 'Failed to create dataset.';
                        onShowNotification(typeof msg === 'string' ? msg : 'Failed to create dataset.', 'error');
                      }
                    }
                  }}
                  disabled={step2Loading}
                >
                  {step2Loading ? (step2ProgressLabel || 'Processing…') : 'Next'}
                </button>
              </div>
            </>
          ) : currentStep === 3 ? (
            // Step 3 (Review & Publish): Back on left, Cancel and Publish on right
            <>
              <button className="wizard-button wizard-button-secondary" onClick={onBack}>
                Back
              </button>
              <div className="wizard-actions-right">
                <button className="wizard-button wizard-button-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="wizard-button wizard-button-primary"
                  onClick={async () => {
                    // PRD-979 (flag OFF): original await-and-close flow. Step 2 already
                    // created + uploaded, so all we do here is fire extractFields and close.
                    if (!autoPublishEnabled) {
                      const ids = datasetIds?.length > 0 ? datasetIds : (datasetId ? [datasetId] : []);
                      if (onExtractFields && ids.length > 0) {
                        try {
                          await Promise.all(ids.map((id) => onExtractFields(id)));
                          if (onShowNotification) {
                            onShowNotification(
                              ids.length === 1 ? 'Dataset indexing has started' : `${ids.length} datasets indexing has started`,
                              'success'
                            );
                          }
                          onClearFormData();
                          onClose();
                        } catch (error) {
                          if (onShowNotification) onShowNotification(error.message || 'Failed to start indexing', 'error');
                        }
                      } else {
                        onNext();
                      }
                      return;
                    }

                    // PRD-979 (flag ON): fire-and-navigate. Create dataset rows synchronously (fast, ~1s),
                    // close the wizard immediately, then upload + start indexing as an unawaited
                    // background promise. The parent component stays mounted so the promise
                    // chain completes even after the modal is gone.
                    const preExistingIds = datasetIds?.length > 0
                      ? datasetIds
                      : (datasetId ? [datasetId] : []);
                    const groupAsOne = formData.groupAsOneDataset;
                    const fileDatasetNames = formData.fileDatasetNames || [];
                    const connectionData = formData.connectionData;
                    const isConnection = connectionData?.selectedSource === 'connection';
                    const nameForGrouped =
                      formData.groupedDatasetName?.trim() || fileDatasetNames[0]?.trim() || '';

                    try {
                      let ids = preExistingIds;
                      // Standard path: no dataset rows exist yet — create them (fast).
                      if (!ids.length) {
                        ids = [];
                        if (groupAsOne) {
                          const resp = await onCreateDataset({
                            name: nameForGrouped,
                            description: nameForGrouped,
                            prompt: '',
                          });
                          if (resp?.id) ids.push(resp.id);
                        } else {
                          for (let i = 0; i < (selectedFiles?.length || 0); i++) {
                            const datasetName = (fileDatasetNames[i] || selectedFiles[i].name).trim();
                            const resp = await onCreateDataset({
                              name: datasetName,
                              description: datasetName,
                              prompt: '',
                            });
                            if (resp?.id) ids.push(resp.id);
                          }
                        }
                        if (onSetDatasetIds) onSetDatasetIds(ids);
                      }
                      if (!ids.length) throw new Error('No dataset was created.');

                      // Close the wizard NOW — the rest runs in the background.
                      const fileCount = selectedFiles?.length || 0;
                      if (onShowNotification) {
                        onShowNotification(
                          fileCount > 0
                            ? `Uploading ${fileCount} file${fileCount === 1 ? '' : 's'} — will appear in Data Sources when ready.`
                            : `Dataset "${nameForGrouped}" is being prepared.`,
                          'success'
                        );
                      }
                      onClearFormData();
                      onClose();

                      // Fire-and-forget: upload files (if not already handled by the module path)
                      // + kick off extraction. Errors surface as a toast.
                      const backgroundIds = [...ids];
                      const backgroundFiles = selectedFiles || [];
                      (async () => {
                        try {
                          let extractIds = backgroundIds;
                          if (!preExistingIds.length) {
                            if (isConnection) {
                              const storageUrl = `https://${connectionData.storageAccount}.blob.core.windows.net/${connectionData.containerName}${connectionData.pathPrefix ? '/' + connectionData.pathPrefix : ''}`;
                              await onUploadFiles(backgroundIds[0], [], 'azure', storageUrl, connectionData.sasToken);
                            } else if (groupAsOne && backgroundFiles.length > 0) {
                              // PRD-1628: a 207 kept the good files; name the rejected ones.
                              notifyRejectedUploads(await onUploadFiles(backgroundIds[0], backgroundFiles, 'local', '', ''), onShowNotification);
                            } else if (!groupAsOne) {
                              // PRD-1628: a rejected file doesn't stop the files after it.
                              const outcomes = [];
                              for (let i = 0; i < backgroundFiles.length; i++) {
                                outcomes.push(await uploadSingleFile(backgroundFiles[i], () => onUploadFiles(backgroundIds[i], [backgroundFiles[i]], 'local', '', '')));
                              }
                              // A dataset whose only file was rejected has nothing to index.
                              extractIds = backgroundIds.filter((_, i) => outcomes[i]?.uploaded.length > 0);
                              notifyRejectedUploads(combineUploadOutcomes(outcomes), onShowNotification);
                            }
                          }
                          if (onExtractFields) {
                            await Promise.all(extractIds.map((id) => onExtractFields(id)));
                          }
                        } catch (err) {
                          console.error('[DataSourceWizard] Background ingestion failed:', err);
                          if (onShowNotification) {
                            onShowNotification(
                              err?.message
                                ? `Ingestion failed: ${err.message}`
                                : 'Ingestion failed. Check the dataset for details.',
                              'error'
                            );
                          }
                        }
                      })();
                    } catch (err) {
                      console.error('[DataSourceWizard] Publish failed:', err);
                      if (onShowNotification) {
                        onShowNotification(err?.message || 'Failed to publish. Please try again.', 'error');
                      }
                    }
                  }}
                  disabled={!onCreateDataset || !onExtractFields}
                >
                  Publish & Start Indexing
                </button>
              </div>
            </>
          ) : (
            // Step 3+: Back on left, Cancel and Next on right
            <>
              <button className="wizard-button wizard-button-secondary" onClick={onBack}>
                Back
              </button>
              <div className="wizard-actions-right">
                <button className="wizard-button wizard-button-secondary" onClick={handleClose}>
                  Cancel
                </button>
                {currentStep < totalSteps && (
                  <button
                    className="wizard-button wizard-button-primary"
                    onClick={onNext}
                  >
                    Next
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSourceWizard;
